import React, { useMemo, useState } from 'react'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { sectionHeaderStyle, sectionStyle } from '@/components/organisms/dashboard/style'
import {
  DashboardReservationResponse,
  Reservation,
  DashboardReservationColumnName,
  RoomNumber as RoomNumberType,
  ReservationDate as ReservationDateType,
} from '@/models/dashboard/dashboard-reservation'
import { putAdminReadReservationNote } from '@/apis/aipass'
import { ReservationNote } from '@/models/dashboard/dashboard-reservation'
import { ApprovedStatusType } from '@/models/reservation-approved-status'
import { CustomerTag as CustomerTagComponent } from '@/components/atoms/customer/customer-tag'
import { CustomerTag as CustomerTagType } from '@/models/dashboard/dashboard-reservation'
import { RoomNumberAndPaxCell } from '@/components/molecules/dashboard/room-number-and-pax-cell'
import { ReservationMemoCell } from '@/components/molecules/dashboard/reservation-memo-cell'
import { GuestInfoCell } from '@/components/molecules/dashboard/guest-info-cell'
import { ApprovedStatusCell } from '@/components/molecules/dashboard/approved-status-cell'
import { Button, ButtonType } from '@/components/atoms/button'
import { CustomizeModal } from '@/components/molecules/dashboard/customize-modal'
import dayjs from 'dayjs'

type ReservationTableProps = {
  data: DashboardReservationResponse | null
  onRefresh?: () => void
}

/**
 * ダッシュボード予約テーブル
 * サーバーから取得した予約情報をテーブル形式で表示
 * 動的カラム構成、カラムの表示/非表示、並び替え、カスタマイズ機能に対応
 */
export const ReservationTable: React.FC<ReservationTableProps> = ({ data, onRefresh }) => {
  const { t } = useTranslation()

  const [isCustomizeModalOpen, setIsCustomizeModalOpen] = useState(false)

  /**
   * 表示対象のカラムを決定
   * サーバーから返却されたカラム設定から、表示対象（isVisible=true）を抽出し、
   * sortOrderで昇順ソートして表示順序を確定する
   */
  const visibleColumns = useMemo(() => {
    if (!data) return []
    return data.columns.filter(col => col.isVisible).sort((a, b) => a.sortOrder - b.sortOrder)
  }, [data])

  /**
   * 表示対象の予約情報を決定
   * displayOrderで昇順ソートしてテーブル行の表示順序を確定する
   */
  const sortedReservations = useMemo(() => {
    if (!data) return []
    return data.reservations.sort((a, b) => a.displayOrder - b.displayOrder)
  }, [data])

  /**
   * セルの内容をレンダリング
   * 各カラムの型に応じて、適切なコンポーネントまたは整形ロジックでセル内容を生成
   * 複雑なデータ型（配列、オブジェクト）は専用セルコンポーネントで表示
   *
   * @param columnName カラム名
   * @param reservation 予約情報
   * @returns レンダリング済みのセル要素
   */
  const renderCellContent = (columnName: DashboardReservationColumnName, reservation: Reservation): React.ReactNode => {
    const value = reservation.dynamicColumns[columnName]

    switch (columnName) {
      // メモセル 予約に紐付いた複数のメモ（フロント、クリーニング、食事）を表示
      case 'memo':
        const memo = value as ReservationNote[]
        if (!memo) {
          return '-'
        }
        return <ReservationMemoCell memoList={memo} />

      // 客室人数セル
      case 'roomNumber':
        const roomNumberAndPax = value as RoomNumberType[]
        return <RoomNumberAndPaxCell roomNumberAndPaxList={roomNumberAndPax} />

      // ゲスト情報セル 宿泊者名と現在の泊数/総泊数を表示
      case 'guestName':
        const guestName = value as string
        const currentNights = reservation.currentNights
        const totalNights = reservation.totalNights
        return <GuestInfoCell guestName={guestName} currentNights={currentNights} totalNights={totalNights} />

      // 顧客タグセル 複数のタグを背景色付きで表示
      case 'customerTags':
        const customerTags = value as CustomerTagType[]
        return (
          <div css={customerTagsCellStyle}>
            {customerTags.map(tag => (
              <CustomerTagComponent key={tag.name} fontColor={tag.fontColor} bgColor={tag.backgroundColor} name={tag.name} />
            ))}
          </div>
        )
      case 'checkinTime':
        const checkinTime = value as string | null
        return <div css={noWrapTextStyle}>{checkinTime || '-'}</div>
      case 'note':
        const note = value as string | null
        // 折り返しのため、noWrapTextStyleを使用しない
        return <div>{note || '-'}</div>
      case 'planName':
        // 折り返しのため、noWrapTextStyleを使用しない
        const planName = value as string | null
        return <div>{planName || '-'}</div>

      // 宿泊ステータスセル ステータスに応じた色付きバッジで表示
      case 'approvedStatus':
        const approvedStatus = value as ApprovedStatusType
        return <ApprovedStatusCell status={approvedStatus} />
      // 泊食条件セル
      case 'meal':
        const meal = value as string | null
        return <div css={noWrapTextStyle}>{meal || '-'}</div>

      // 売上合計セル
      case 'totalSales':
        const totalSales = value as number | null
        return <div>{totalSales ? `¥${totalSales.toLocaleString()}` : '-'}</div>

      // 未収金セル
      case 'totalUnpaid':
        const totalUnpaid = value as number | null
        return <div css={totalUnpaid && totalUnpaidStyle}>{totalUnpaid ? `¥${totalUnpaid.toLocaleString()}` : '-'}</div>

      // チャネル名セル
      case 'channelName':
        const channelName = value as string
        return <div css={noWrapTextStyle}>{channelName || '-'}</div>
      case 'reservationDate':
        const reservationDate = value as ReservationDateType
        return (
          <div css={reservationDateCellStyle}>
            <div css={noWrapTextStyle}>
              {t('Reserve')} :{' '}
              {reservationDate.reservationDate ? dayjs(reservationDate.reservationDate).format(t('YYYY-MM-DD HH:mm')) : '-'}
            </div>
            <div css={noWrapTextStyle}>
              {t('updated')} :{' '}
              {reservationDate.statusUpdatedAt ? dayjs(reservationDate.statusUpdatedAt).format(t('YYYY-MM-DD HH:mm')) : '-'}
            </div>
          </div>
        )
      default:
        return '-'
    }
  }

  /**
   * カラム名から言語キーを生成して、各言語のラベルを取得
   */
  const getColumnLabel = (columnName: DashboardReservationColumnName): string => {
    const key = `dashboard.columns.${columnName}`
    return t(key)
  }

  /**
   * カスタマイズモーダルの保存完了後、テーブルデータを再取得して画面を更新
   */
  const handleSave = () => {
    if (onRefresh) {
      onRefresh()
    }
  }

  return (
    <div css={reservationTableSectionStyle}>
      {/* ヘッダー タイトル&モーダル表示ボタン */}
      <div css={[sectionHeaderStyle, { flexShrink: 0, justifyContent: 'space-between' }]}>
        <div>{t('Indicators.ReservationDetails')}</div>
        <Button buttonType={ButtonType.Simple} width={130} height={32} fontSize={12} onClick={() => setIsCustomizeModalOpen(true)}>
          {t('dashboard.customizeItem')}
        </Button>
      </div>

      {/* テーブル 横スクロール対応 */}
      <div css={scrollContentWrapper}>
        {/* テーブルヘッダー 表示対象カラムを sortOrder で並び替えて表示 */}
        <div css={headerWrapperStyle}>
          {data &&
            data.columns.length > 0 &&
            visibleColumns.map(col => (
              <div key={col.id} className={`col-${col.columnName}`} css={getColumnHeaderStyle(col.columnName)}>
                {getColumnLabel(col.columnName)}
              </div>
            ))}
        </div>

        {/* テーブルボディ 各行は予約情報を表示 */}
        <div css={tableBodyWrapperStyle}>
          {data &&
            data.reservations.length > 0 &&
            sortedReservations.map(reservation => (
              <a
                href={`/reservation/${reservation.id}?reservationId=${reservation.reservationId}`}
                key={reservation.reservationId}
                onClick={async () => {
                  // 予約をクリックした際、未読メモを既読状態に更新
                  await putAdminReadReservationNote(reservation.reservationId)
                }}
              >
                <div key={reservation.reservationId} css={tableRowStyle}>
                  {/* 表示対象の各カラムについて、対応するセルコンポーネントをレンダリング */}
                  {visibleColumns.map(col => (
                    <div key={`${reservation.reservationId}-${col.columnName}`} css={getColumnCellCommonStyle(col.columnName)}>
                      {renderCellContent(col.columnName, reservation)}
                    </div>
                  ))}
                </div>
              </a>
            ))}
        </div>
      </div>

      {/* カスタマイズモーダル カラムの表示/非表示、並び替え、フィルター設定 */}
      {isCustomizeModalOpen && data && (
        <CustomizeModal
          defaultColumns={data.columns}
          defaultFilter={data.filterType}
          onClose={() => setIsCustomizeModalOpen(false)}
          onSave={handleSave}
        />
      )}
    </div>
  )
}

// ===== Styles =====

const columnWidthConfig: Record<DashboardReservationColumnName, number> = {
  roomNumber: 180,
  guestName: 150,
  customerTags: 200,
  memo: 500,
  checkinTime: 100,
  planName: 195,
  totalSales: 100,
  totalUnpaid: 100,
  channelName: 150,
  note: 220,
  approvedStatus: 150,
  meal: 100,
  reservationDate: 200,
}

const reservationTableSectionStyle = css(sectionStyle, {
  width: '100%',
  height: 'calc(100vh - 60px)',
  display: 'flex',
  flexDirection: 'column',
})

const scrollContentWrapper = css({
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
  overflow: 'auto',
})

const headerWrapperStyle = css({
  display: 'flex',
  alignItems: 'center',
  height: 28,
  background: '#FAFAFA',
  borderTop: '1px solid #F2F2F2',
  borderBottom: '1px solid #F2F2F2',
  fontSize: 10,
  color: '#676767',
  fontWeight: 'bold',
  padding: '0 40px 0 30px',
  flexShrink: 0,
  minWidth: 'max-content',
  position: 'sticky',
  top: 0,
  zIndex: 10,
  gap: 6,
})

const tableBodyWrapperStyle = css({
  minWidth: 'max-content',
  padding: 10,
})

const tableRowStyle = css({
  display: 'flex',
  alignItems: 'flex-start',
  fontSize: 12,
  fontWeight: 'normal',
  padding: '16px 20px',
  color: '#272727',
  borderBottom: '1px solid #F2F2F2',
  '&:hover': {
    background: '#FAFAFA',
  },
  gap: 6,
})

/**
 * ヘッダーセルのスタイルを取得
 * 各カラムの幅を固定値で設定
 */
const getColumnHeaderStyle = (columnName: DashboardReservationColumnName) => {
  const width = columnWidthConfig[columnName]
  return css({
    width: `${width}px`,
  })
}

/**
 * セルの共通のCSS
 */
const getColumnCellCommonStyle = (columnName: DashboardReservationColumnName) => {
  const width = columnWidthConfig[columnName]

  return css({
    width: `${width}px`,
    gap: 4,
    fontWeight: 'bold',
  })
}

const customerTagsCellStyle = css({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 8,
})

const noWrapTextStyle = css({
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
})

const totalUnpaidStyle = css({
  color: '#f2a40b',
})

const reservationDateCellStyle = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
})
